import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://uandfashion.eu';

const BlogHubPage = () => {
  const { language, t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/posts`)
      .then((res) => setPosts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-hub-page">
      <SEO
        title={t('Inspiratie — Style Journal | U&Fashion', 'Inspiration — Style Journal | U&Fashion')}
        description={t(
          'Stijltips, seizoensgidsen en outfit-inspiratie voor de vrouw van 35 tot 55.',
          'Style tips, seasonal guides and outfit inspiration for women aged 35 to 55.'
        )}
        canonical={`${SITE_URL}/inspiratie`}
      />
      <section className="bg-gradient-to-br from-pink-50/60 via-purple-50/40 to-indigo-50/40 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-14 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-pink-500 font-medium mb-4">
            {t('Style Journal', 'Style Journal')}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-gray-900 leading-[1.1] tracking-tight mb-6">
            {t('Inspiratie', 'Inspiration')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-light max-w-2xl mx-auto">
            {t(
              'Stijltips, seizoensgidsen en outfit-inspiratie voor de stijlbewuste vrouw van 35 tot 55.',
              'Style tips, seasonal guides and outfit inspiration for the style-conscious woman aged 35 to 55.'
            )}
          </p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        {loading ? (
          <div className="text-center text-gray-500 py-12">{t('Laden…', 'Loading…')}</div>
        ) : posts.length === 0 ? (
          <div className="text-center text-gray-500 py-16" data-testid="blog-hub-empty">
            <p className="text-lg mb-3">{t('Nog geen artikels beschikbaar.', 'No articles available yet.')}</p>
            <p className="text-sm text-gray-400">
              {t('Het eerste artikel komt binnenkort.', 'The first article is coming soon.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-hub-grid">
            {posts.map((post) => {
              const title = language === 'nl' ? post.title_nl : (post.title_en || post.title_nl);
              const excerpt = language === 'nl'
                ? (post.excerpt_nl || post.excerpt_en || '')
                : (post.excerpt_en || post.excerpt_nl || '');
              return (
                <Link
                  key={post.id}
                  to={`/inspiratie/${post.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-gray-100"
                  data-testid={`blog-hub-post-${post.slug}`}
                >
                  <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                    <img src={post.hero_image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <h2 className="text-lg font-medium leading-tight text-gray-900 group-hover:text-pink-600 transition-colors mb-2">
                      {title}
                    </h2>
                    {excerpt && <p className="text-sm text-gray-600 line-clamp-3">{excerpt}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default BlogHubPage;
